import React, { createContext, useContext, useState, useEffect } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';

interface ThemeContextType {
  isDarkMode: boolean;
  toggleTheme: () => void;
  setTheme: (isDark: boolean) => void;
  colors: typeof appColors;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

interface ThemeProviderWrapperProps {
  children: React.ReactNode;
}

// Centralized color palette
const appColors = {
  // Original colors from colors.ts
  Background: "#24272C",
  Light_Shadow: "#FFFFFF",
  Dark_Shadow: "#000000",
  Label: "#7F8493",
  Gradient: "#3B82F6",
  Gradient_2: "#1E40AF",
  Opacity_shadow: "rgba(0, 0, 0, 0.25)",
  Slider_Fill: "#887400",
  ButtonColor: "#1E1F20",
  ButtonTextColor: "#9999A0",

  // Additional colors from the app (Mockdash-aligned)
  MainBackground: "#07090e",
  CardBackground: "rgba(16, 23, 40, 0.6)",
  HeaderBackground: "rgba(10, 10, 10, 0.8)",
  TextPrimary: "#f8fafc",
  TextSecondary: "#9ca3af",
  TextMuted: "#7F8493",
  Success: "#10B981",
  Warning: "#F59E0B",
  Error: "#EF4444",
  Info: "#22D3EE", // Cyan

  // Status colors
  Online: "#10B981",
  Offline: "#EF4444",
  Pending: "#F59E0B",

  // Gradient colors
  GradientStart: "#22D3EE", // Cyan
  GradientEnd: "#0891B2", // Darker Cyan

  // Light theme specific colors - Grayscale Theme
  LightBackground: "#F8FAFC",
  LightCardBackground: "#FFFFFF",
  LightSidebarBackground: "#FFFFFF",
  LightBorderColor: "#E2E8F0",
  LightTextPrimary: "#1E293B",
  LightTextSecondary: "#64748B",
  LightTextMuted: "#94A3B8",
  LightAccent: "#000000",
  LightAccentHover: "#1E293B",
};

// Light theme
const lightTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: appColors.LightAccent,
      light: '#818CF8',
      dark: appColors.LightAccentHover,
    },
    secondary: {
      main: appColors.Gradient_2,
      light: '#A78BFA',
      dark: '#6B46C1',
    },
    background: {
      default: appColors.LightBackground,
      paper: appColors.LightCardBackground,
    },
    text: {
      primary: appColors.LightTextPrimary,
      secondary: appColors.LightTextSecondary,
    },
    success: {
      main: appColors.Success,
      light: '#34D399',
      dark: '#059669',
    },
    warning: {
      main: appColors.Warning,
      light: '#FBBF24',
      dark: '#D97706',
    },
    error: {
      main: appColors.Error,
      light: '#F87171',
      dark: '#DC2626',
    },
    info: {
      main: appColors.Info,
      light: '#60A5FA',
      dark: '#2563EB',
    },
    divider: appColors.LightBorderColor,
    action: {
      hover: 'rgba(59, 130, 246, 0.04)',
      selected: 'rgba(59, 130, 246, 0.08)',
    },
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: appColors.LightCardBackground,
          color: appColors.LightTextPrimary,
          border: `1px solid ${appColors.LightBorderColor}`,
          borderRadius: '16px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05), 0 1px 2px rgba(0, 0, 0, 0.1)',
          transition: 'all 0.2s ease-in-out',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: appColors.LightCardBackground,
          color: appColors.LightTextPrimary,
          border: `1px solid ${appColors.LightBorderColor}`,
          borderRadius: '16px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05), 0 1px 2px rgba(0, 0, 0, 0.1)',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05), 0 2px 4px rgba(0, 0, 0, 0.1)',
            transform: 'translateY(-1px)',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '16px',
          textTransform: 'none',
          fontWeight: 600,
          padding: '10px 24px',
          fontSize: '0.875rem',
          transition: 'all 0.2s ease-in-out',
        },
        contained: {
          backgroundColor: appColors.LightAccent,
          color: '#FFFFFF',
          boxShadow: '0 1px 3px rgba(59, 130, 246, 0.2)',
          '&:hover': {
            backgroundColor: appColors.LightAccentHover,
            boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
            transform: 'translateY(-1px)',
          },
          '&:active': {
            transform: 'translateY(0)',
          },
        },
        outlined: {
          backgroundColor: 'transparent',
          border: `2px solid ${appColors.LightAccent}`,
          color: appColors.LightAccent,
          '&:hover': {
            backgroundColor: 'rgba(59, 130, 246, 0.04)',
            border: `2px solid ${appColors.LightAccentHover}`,
            color: appColors.LightAccentHover,
          },
        },
        text: {
          color: appColors.LightAccent,
          '&:hover': {
            backgroundColor: 'rgba(59, 130, 246, 0.04)',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            backgroundColor: appColors.LightCardBackground,
            borderRadius: '12px',
            transition: 'all 0.2s ease-in-out',
            '& fieldset': {
              borderColor: appColors.LightBorderColor,
              borderWidth: '2px',
            },
            '&:hover fieldset': {
              borderColor: appColors.LightAccent,
            },
            '&.Mui-focused fieldset': {
              borderColor: appColors.LightAccent,
              boxShadow: `0 0 0 3px rgba(59, 130, 246, 0.1)`,
            },
          },
          '& .MuiInputLabel-root': {
            color: appColors.LightTextSecondary,
            fontWeight: 500,
          },
          '& .MuiInputLabel-root.Mui-focused': {
            color: appColors.LightAccent,
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          backgroundColor: appColors.LightCardBackground,
          borderRadius: '12px',
          transition: 'all 0.2s ease-in-out',
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: appColors.LightBorderColor,
            borderWidth: '2px',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: appColors.LightAccent,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: appColors.LightAccent,
            boxShadow: `0 0 0 3px rgba(102, 126, 234, 0.1)`,
          },
        },
        select: {
          '&:focus': {
            backgroundColor: 'transparent',
          },
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundColor: appColors.LightCardBackground,
          color: appColors.LightTextPrimary,
          border: `1px solid ${appColors.LightBorderColor}`,
          borderRadius: '12px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08), 0 2px 8px rgba(0, 0, 0, 0.04)',
          marginTop: '8px',
          zIndex: 1300,
        },
        list: {
          padding: '8px 0',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          color: appColors.LightTextPrimary,
          padding: '12px 16px',
          cursor: 'pointer',
          pointerEvents: 'auto',
          borderRadius: '8px',
          margin: '2px 8px',
          transition: 'all 0.15s ease-in-out',
          '&:hover': {
            backgroundColor: 'rgba(102, 126, 234, 0.08)',
            color: appColors.LightAccent,
          },
          '&.Mui-selected': {
            backgroundColor: 'rgba(102, 126, 234, 0.12)',
            color: appColors.LightAccent,
            fontWeight: 600,
            '&:hover': {
              backgroundColor: 'rgba(102, 126, 234, 0.16)',
            },
          },
          '&.Mui-focusVisible': {
            backgroundColor: 'rgba(102, 126, 234, 0.08)',
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundColor: appColors.LightCardBackground,
          color: appColors.LightTextPrimary,
          border: `1px solid ${appColors.LightBorderColor}`,
          borderRadius: '20px',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          color: appColors.LightTextPrimary,
          fontWeight: 600,
          fontSize: '1.25rem',
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          color: appColors.LightTextPrimary,
        },
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: {
          '& .MuiInputLabel-root': {
            color: '#666666',
          },
          '& .MuiInputLabel-root.Mui-focused': {
            color: appColors.Gradient,
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          backgroundColor: appColors.LightCardBackground,
          borderRadius: '12px',
          border: `1px solid ${appColors.LightBorderColor}`,
          padding: '4px',
        },
        indicator: {
          backgroundColor: appColors.LightAccent,
          height: '3px',
          borderRadius: '2px',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          color: appColors.LightTextSecondary,
          fontWeight: 500,
          textTransform: 'none',
          borderRadius: '8px',
          margin: '0 2px',
          transition: 'all 0.2s ease-in-out',
          '&.Mui-selected': {
            color: appColors.LightAccent,
            fontWeight: 600,
            backgroundColor: 'rgba(102, 126, 234, 0.08)',
          },
          '&:hover': {
            color: appColors.LightTextPrimary,
            backgroundColor: 'rgba(102, 126, 234, 0.04)',
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: appColors.LightCardBackground,
          color: appColors.LightTextPrimary,
          border: 'none',
          borderBottom: `1px solid ${appColors.LightBorderColor}`,
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          '& img': {
            objectFit: 'cover',
            width: '100%',
            height: '100%',
          },
          '& .MuiAvatar-img': {
            objectFit: 'cover',
            width: '100%',
            height: '100%',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(102, 126, 234, 0.1)',
          color: appColors.LightAccent,
          border: `1px solid rgba(102, 126, 234, 0.2)`,
          borderRadius: '8px',
          fontWeight: 500,
          transition: 'all 0.2s ease-in-out',
        },
        filled: {
          backgroundColor: 'rgba(102, 126, 234, 0.1)',
          '&:hover': {
            backgroundColor: 'rgba(102, 126, 234, 0.15)',
            transform: 'translateY(-1px)',
          },
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          color: appColors.LightTextPrimary,
        },
        h1: {
          color: appColors.LightTextPrimary,
          fontWeight: 700,
        },
        h2: {
          color: appColors.LightTextPrimary,
          fontWeight: 700,
        },
        h3: {
          color: appColors.LightTextPrimary,
          fontWeight: 600,
        },
        h4: {
          color: appColors.LightTextPrimary,
          fontWeight: 600,
        },
        h5: {
          color: appColors.LightTextPrimary,
          fontWeight: 600,
        },
        h6: {
          color: appColors.LightTextPrimary,
          fontWeight: 600,
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          color: appColors.LightTextSecondary,
          borderRadius: '8px',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            backgroundColor: 'rgba(102, 126, 234, 0.08)',
            color: appColors.LightAccent,
          },
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          backgroundColor: appColors.LightCardBackground,
          borderRadius: '12px',
          border: `1px solid ${appColors.LightBorderColor}`,
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
        },
      },
    },
    MuiTable: {
      styleOverrides: {
        root: {
          backgroundColor: 'transparent',
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(102, 126, 234, 0.02)',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          color: appColors.LightTextPrimary,
          borderBottom: `1px solid ${appColors.LightBorderColor}`,
          padding: '16px',
        },
        head: {
          color: appColors.LightTextPrimary,
          fontWeight: 600,
          backgroundColor: 'rgba(102, 126, 234, 0.02)',
          fontSize: '0.875rem',
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: '12px',
          border: '1px solid',
          fontWeight: 500,
        },
        standardSuccess: {
          backgroundColor: 'rgba(16, 185, 129, 0.1)',
          color: '#065f46',
          borderColor: 'rgba(16, 185, 129, 0.3)',
        },
        standardError: {
          backgroundColor: 'rgba(239, 68, 68, 0.1)',
          color: '#991b1b',
          borderColor: 'rgba(239, 68, 68, 0.3)',
        },
        standardWarning: {
          backgroundColor: 'rgba(245, 158, 11, 0.1)',
          color: '#92400e',
          borderColor: 'rgba(245, 158, 11, 0.3)',
        },
        standardInfo: {
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          color: '#1e40af',
          borderColor: 'rgba(59, 130, 246, 0.3)',
        },
      },
    },
    MuiSnackbar: {
      styleOverrides: {
        root: {
          '& .MuiSnackbarContent-root': {
            backgroundColor: appColors.LightTextPrimary,
            color: appColors.LightCardBackground,
            borderRadius: '12px',
            border: `1px solid ${appColors.LightBorderColor}`,
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          },
        },
      },
    }
  },
});

// Dark theme with glassmorphic design
const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: appColors.Gradient,
    },
    secondary: {
      main: appColors.Gradient_2,
    },
    background: {
      default: appColors.MainBackground,
      paper: appColors.CardBackground,
    },
    text: {
      primary: appColors.TextPrimary,
      secondary: appColors.TextSecondary,
    },
    success: {
      main: appColors.Success,
    },
    warning: {
      main: appColors.Warning,
    },
    error: {
      main: appColors.Error,
    },
    info: {
      main: appColors.Info,
    },
    divider: 'rgba(255, 255, 255, 0.05)',
    action: {
      hover: 'rgba(255, 255, 255, 0.03)',
      selected: 'rgba(255, 255, 255, 0.05)',
    },
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          background: 'linear-gradient(180deg, rgba(20, 24, 32, 0.85) 0%, rgba(5, 7, 10, 0.95) 100%)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          border: '1px solid rgba(34, 211, 238, 0.25)',
          borderTop: '1px solid rgba(34, 211, 238, 0.5)',
          borderLeft: '1px solid rgba(34, 211, 238, 0.4)',
          borderRadius: '12px',
          color: '#ffffff',
          boxShadow: 'inset 0 1px 1px rgba(255, 255, 255, 0.1), 0 10px 30px rgba(0, 0, 0, 0.8), inset 0 0 20px rgba(34, 211, 238, 0.05), 0 0 15px rgba(34, 211, 238, 0.1)',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          background: 'linear-gradient(180deg, rgba(20, 24, 32, 0.85) 0%, rgba(5, 7, 10, 0.95) 100%)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          border: '1px solid rgba(34, 211, 238, 0.25)',
          borderTop: '1px solid rgba(34, 211, 238, 0.5)',
          borderLeft: '1px solid rgba(34, 211, 238, 0.4)',
          borderRadius: '12px',
          color: '#ffffff',
          boxShadow: 'inset 0 1px 1px rgba(255, 255, 255, 0.1), 0 10px 30px rgba(0, 0, 0, 0.8), inset 0 0 20px rgba(34, 211, 238, 0.05), 0 0 15px rgba(34, 211, 238, 0.1)',
          transition: 'all 0.3s ease',
          '&:hover': {
            borderColor: 'rgba(34, 211, 238, 0.35)',
            boxShadow: 'inset 0 1px 1px rgba(255, 255, 255, 0.1), 0 12px 36px rgba(0, 0, 0, 0.8), inset 0 0 24px rgba(34, 211, 238, 0.08), 0 0 20px rgba(34, 211, 238, 0.15)',
          },
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: '24px',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '12px',
          textTransform: 'none',
          fontWeight: 600,
          fontFamily: "'Inter', sans-serif",
          padding: '14px 32px',
          minHeight: '48px',
          transition: 'all 0.2s ease',
        },
        contained: {
          backgroundColor: 'rgba(10, 10, 10, 0.9)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          border: '1.5px solid rgba(34, 211, 238, 0.6)',
          color: '#ffffff',
          boxShadow: '0 0 15px rgba(34, 211, 238, 0.2), inset 0 1px 0 0 rgba(255,255,255,0.08)',
          '&:hover': {
            backgroundColor: 'rgba(26, 26, 26, 0.95)',
            borderColor: '#22d3ee',
            boxShadow: '0 0 25px rgba(34, 211, 238, 0.5), inset 0 1px 0 0 rgba(255,255,255,0.1)',
            transform: 'translateY(-1px)',
          },
          '&:active': {
            transform: 'scale(0.98)',
          },
        },
        outlined: {
          backgroundColor: 'rgba(255, 255, 255, 0.04)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          border: '1.5px solid rgba(34, 211, 238, 0.4)',
          color: '#22d3ee',
          boxShadow: 'inset 0 1px 0 0 rgba(255,255,255,0.05)',
          '&:hover': {
            backgroundColor: 'rgba(34, 211, 238, 0.1)',
            border: '1.5px solid rgba(34, 211, 238, 0.6)',
            boxShadow: 'inset 0 1px 0 0 rgba(255,255,255,0.06)',
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(255, 255, 255, 0.02)',
          backdropFilter: 'blur(12px)',
          borderRadius: '12px',
          border: '1px solid rgba(34, 211, 238, 0.2)',
        },
        indicator: {
          backgroundColor: '#22d3ee', // Cyan
          height: '3px',
          borderRadius: '2px',
          boxShadow: '0 0 10px rgba(34, 211, 238, 0.6)', // Glowing indicator
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          color: '#9ca3af',
          fontWeight: 500,
          fontSize: '14px',
          fontFamily: "'Inter', sans-serif",
          textTransform: 'none',
          transition: 'all 0.2s ease',
          '&.Mui-selected': {
            color: '#f8fafc',
          },
          '&:hover': {
            color: '#f8fafc',
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            backdropFilter: 'blur(20px)',
            borderRadius: '16px',
            transition: 'all 0.3s ease',
            boxShadow: 'inset 0 1px 0 0 rgba(255,255,255,0.04)',
            '& fieldset': {
              borderColor: 'rgba(255, 255, 255, 0.1)',
            },
            '&:hover fieldset': {
              borderColor: 'rgba(34, 211, 238, 0.4)', // Cyan hover
            },
            '&.Mui-focused fieldset': {
              borderColor: '#22d3ee', // Cyan focus
              borderWidth: '2px',
              boxShadow: '0 0 10px rgba(34, 211, 238, 0.2)',
            },
          },
          '& .MuiInputLabel-root': {
            color: 'rgba(255, 255, 255, 0.6)',
            fontFamily: "'Inter', sans-serif",
          },
          '& .MuiInputLabel-root.Mui-focused': {
            color: '#22d3ee',
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(20px)',
          borderRadius: '16px',
          transition: 'all 0.3s ease',
          boxShadow: 'inset 0 1px 0 0 rgba(255,255,255,0.04)',
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(255, 255, 255, 0.1)',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(34, 211, 238, 0.4)',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#22d3ee',
            borderWidth: '2px',
            boxShadow: '0 0 10px rgba(34, 211, 238, 0.2)',
          },
        },
        select: {
          '&:focus': {
            backgroundColor: 'transparent',
          },
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          background: 'linear-gradient(180deg, rgba(20, 24, 32, 0.95) 0%, rgba(5, 7, 10, 0.98) 100%)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          border: '1px solid rgba(34, 211, 238, 0.25)',
          borderRadius: '12px',
          boxShadow: 'inset 0 1px 1px rgba(255, 255, 255, 0.1), 0 10px 30px rgba(0, 0, 0, 0.8), inset 0 0 20px rgba(34, 211, 238, 0.05)',
          marginTop: '4px',
          zIndex: 1300,
        },
        list: {
          padding: '8px 0',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          color: '#ffffff',
          padding: '12px 16px',
          cursor: 'pointer',
          pointerEvents: 'auto',
          fontFamily: "'Inter', sans-serif",
          transition: 'all 0.2s',
          '&:hover': {
            backgroundColor: 'rgba(34, 211, 238, 0.15)',
          },
          '&.Mui-selected': {
            backgroundColor: 'rgba(34, 211, 238, 0.2)',
            borderLeft: '2px solid #22d3ee',
            '&:hover': {
              backgroundColor: 'rgba(34, 211, 238, 0.25)',
            },
          },
          '&.Mui-focusVisible': {
            backgroundColor: 'rgba(34, 211, 238, 0.1)',
          },
        },
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: {
          '& .MuiInputLabel-root': {
            color: 'rgba(255, 255, 255, 0.7)',
          },
          '& .MuiInputLabel-root.Mui-focused': {
            color: '#22d3ee',
          },
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          color: '#ffffff',
          fontWeight: 600,
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          color: '#ffffff',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          background: 'linear-gradient(180deg, rgba(20, 24, 32, 0.95) 0%, rgba(5, 7, 10, 0.98) 100%)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          border: '1px solid rgba(34, 211, 238, 0.25)',
          borderTop: '1px solid rgba(34, 211, 238, 0.5)',
          borderLeft: '1px solid rgba(34, 211, 238, 0.4)',
          borderRadius: '12px',
          boxShadow: 'inset 0 1px 1px rgba(255, 255, 255, 0.1), 0 10px 30px rgba(0, 0, 0, 0.8), inset 0 0 20px rgba(34, 211, 238, 0.05), 0 0 15px rgba(34, 211, 238, 0.1)',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          background: 'rgba(16, 23, 40, 0.4)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          border: '1px solid rgba(34, 211, 238, 0.25)',
          borderTop: '1px solid rgba(34, 211, 238, 0.5)',
          borderLeft: '1px solid rgba(34, 211, 238, 0.4)',
          borderRadius: '12px',
          boxShadow: 'inset 0 1px 1px rgba(255, 255, 255, 0.1), 0 10px 30px rgba(0, 0, 0, 0.8), inset 0 0 20px rgba(34, 211, 238, 0.05), 0 0 15px rgba(34, 211, 238, 0.1)',
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          '& img': {
            objectFit: 'cover',
            width: '100%',
            height: '100%',
          },
          '& .MuiAvatar-img': {
            objectFit: 'cover',
            width: '100%',
            height: '100%',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(34, 211, 238, 0.12)',
          backdropFilter: 'blur(8px)',
          color: '#ffffff',
          border: '1px solid rgba(34, 211, 238, 0.3)',
          borderRadius: '10px',
          fontWeight: 500,
        },
        filled: {
          backgroundColor: 'rgba(34, 211, 238, 0.12)',
          '&:hover': {
            backgroundColor: 'rgba(34, 211, 238, 0.2)',
          },
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          color: '#ffffff',
        },
        h1: {
          color: '#ffffff',
          fontWeight: 700,
          fontSize: '2rem',
        },
        h2: {
          color: '#ffffff',
          fontWeight: 700,
          fontSize: '1.75rem',
        },
        h3: {
          color: '#ffffff',
          fontWeight: 600,
          fontSize: '1.5rem',
        },
        h4: {
          color: '#ffffff',
          fontWeight: 600,
          fontSize: '1.25rem',
        },
        h5: {
          color: '#ffffff',
          fontWeight: 600,
          fontSize: '1.125rem',
        },
        h6: {
          color: '#ffffff',
          fontWeight: 600,
          fontSize: '1rem',
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          color: '#ffffff',
          transition: 'all 0.2s ease',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.06)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
            boxShadow: 'inset 0 1px 0 0 rgba(255,255,255,0.05)',
          },
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          background: 'linear-gradient(180deg, rgba(20, 24, 32, 0.85) 0%, rgba(5, 7, 10, 0.95) 100%)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          border: '1px solid rgba(34, 211, 238, 0.25)',
          borderTop: '1px solid rgba(34, 211, 238, 0.5)',
          borderLeft: '1px solid rgba(34, 211, 238, 0.4)',
          borderRadius: '12px',
          boxShadow: 'inset 0 1px 1px rgba(255, 255, 255, 0.1), 0 10px 30px rgba(0, 0, 0, 0.8), inset 0 0 20px rgba(34, 211, 238, 0.05), 0 0 15px rgba(34, 211, 238, 0.1)',
        },
      },
    },
    MuiTable: {
      styleOverrides: {
        root: {
          backgroundColor: 'transparent',
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(255, 255, 255, 0.02)',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          color: '#ffffff',
          borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
        },
        head: {
          color: '#ffffff',
          fontWeight: 600,
          backgroundColor: 'rgba(255, 255, 255, 0.02)',
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: '12px',
          border: '1px solid',
          backdropFilter: 'blur(20px)',
        },
        standardSuccess: {
          backgroundColor: 'rgba(76, 175, 80, 0.15)',
          color: '#81c784',
          borderColor: 'rgba(76, 175, 80, 0.3)',
        },
        standardError: {
          backgroundColor: 'rgba(244, 67, 54, 0.15)',
          color: '#e57373',
          borderColor: 'rgba(244, 67, 54, 0.3)',
        },
        standardWarning: {
          backgroundColor: 'rgba(255, 152, 0, 0.15)',
          color: '#ffb74d',
          borderColor: 'rgba(255, 152, 0, 0.3)',
        },
        standardInfo: {
          backgroundColor: 'rgba(34, 211, 238, 0.15)',
          color: '#67e8f9',
          borderColor: 'rgba(34, 211, 238, 0.3)',
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: 'rgba(26, 26, 26, 0.95)',
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: '12px',
          color: '#ffffff',
          boxShadow: '0 8px 24px rgba(0,0,0,0.4), inset 0 1px 0 0 rgba(255,255,255,0.05)',
        },
        arrow: {
          color: 'rgba(26, 26, 26, 0.95)',
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: {
          '& .MuiSwitch-switchBase.Mui-checked': {
            color: '#22d3ee',
            '&:hover': {
              backgroundColor: 'rgba(34, 211, 238, 0.08)',
            },
          },
          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
            backgroundColor: 'rgba(34, 211, 238, 0.5)',
            opacity: 1,
          },
        },
        track: {
          backgroundColor: 'rgba(255, 255, 255, 0.06)',
          border: '1px solid rgba(255, 255, 255, 0.08)',
          borderRadius: '14px',
        },
        thumb: {
          '&.Mui-checked': {
            color: '#22d3ee',
          },
        },
      },
    },
    MuiSlider: {
      styleOverrides: {
        root: {
          color: '#22d3ee',
          '& .MuiSlider-thumb': {
            '&:focus, &:hover, &.Mui-focusVisible': {
              boxShadow: '0 0 0 8px rgba(34, 211, 238, 0.16)',
            },
          },
        },
        track: {
          border: 'none',
        },
        rail: {
          opacity: 0.3,
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(255, 255, 255, 0.06)',
          borderRadius: '4px',
          overflow: 'hidden',
        },
        bar: {
          backgroundColor: '#22d3ee',
          borderRadius: '4px',
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: 'rgba(255, 255, 255, 0.08)',
        },
      },
    },
    MuiSnackbar: {
      styleOverrides: {
        root: {
          '& .MuiSnackbarContent-root': {
            backgroundColor: 'rgba(26, 26, 26, 0.95)',
            color: '#ffffff',
            borderRadius: '12px',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.05)',
          },
        },
      },
    }
  },
});

export const ThemeProviderWrapper: React.FC<ThemeProviderWrapperProps> = ({ children }) => {
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme === 'light') return false;
    if (savedTheme === 'dark') return true;
    return true; // default: dark when no preference stored
  });

  useEffect(() => {
    // Save theme preference to localStorage
    localStorage.setItem('theme', isDarkMode ? 'dark' : 'light');
  }, [isDarkMode]);

  const toggleTheme = () => {
    setIsDarkMode(prev => !prev);
  };

  const setTheme = (isDark: boolean) => {
    setIsDarkMode(isDark);
  };

  const theme = isDarkMode ? darkTheme : lightTheme;

  // Theme-aware colors that change based on the current theme
  const themeAwareColors = {
    ...appColors,
    // Override colors based on theme (dark: Mockdash-aligned)
    Background: isDarkMode ? "#07090e" : appColors.LightBackground,
    CardBackground: isDarkMode ? "rgba(16, 23, 40, 0.6)" : appColors.LightCardBackground,
    HeaderBackground: isDarkMode ? "#07090e" : appColors.LightSidebarBackground,
    TextPrimary: isDarkMode ? "#f8fafc" : appColors.LightTextPrimary,
    TextSecondary: isDarkMode ? "#9ca3af" : appColors.LightTextSecondary,
    TextMuted: isDarkMode ? "#6b7280" : appColors.LightTextMuted,
    BorderColor: isDarkMode ? "rgba(34, 211, 238, 0.25)" : appColors.LightBorderColor,
    Accent: isDarkMode ? appColors.Gradient : appColors.LightAccent,
    AccentHover: isDarkMode ? appColors.GradientEnd : appColors.LightAccentHover,
  };

  const contextValue: ThemeContextType = {
    isDarkMode,
    toggleTheme,
    setTheme,
    colors: themeAwareColors,
  };

  return (
    <ThemeContext.Provider value={contextValue}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeContext.Provider>
  );
};
